import {
    selectTotalArchivedChatsCount,
    selectChannelChatsCount,
    selectIsArchivedView,
    selectIsChannelsView,
} from "./redux/TeamInboxSelectors";
import { TeamInboxActions } from "./redux/TeamInboxReducer";
import { useAppDispatch, useAppSelector } from "@common/reducers/hooks/store.hook";
import {
    Archive as ArchiveIcon,
    Campaign as CampaignIcon,
    DoneAll as DoneAllIcon,
    RemoveDone as RemoveDoneIcon,
} from '@mui/icons-material';
import { Box, Stack, Typography } from "@mui/material";
import React from "react";
import { useTranslation } from 'react-i18next';
import {
    isSendSeenToggleVisible,
    setSendSeenEnabled,
    useSendSeenEnabled,
} from './send-seen-preference';

type FolderPillProps = {
    label: string;
    count?: number;
    active: boolean;
    icon: React.ReactNode;
    onClick: () => void;
    title?: string;
};

// WhatsApp Web filter chip: pill, no border, neutral fill when idle and a
// green-tinted fill with accent text when active.
const FolderPill = ({ label, count, active, icon, onClick, title }: FolderPillProps) => (
    <Box
        onClick={onClick}
        title={title}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            height: '32px',
            paddingInline: '12px',
            borderRadius: '999px',
            cursor: 'pointer',
            flexShrink: 0,
            whiteSpace: 'nowrap',
            border: 'none',
            color: active ? 'var(--wa-accent)' : 'var(--wa-secondary-text)',
            backgroundColor: active ? 'var(--wa-chip-selected-bg)' : 'var(--wa-search-bg)',
            transition: 'background-color 0.15s ease',
            '&:hover': {
                backgroundColor: active
                    ? 'var(--wa-chip-selected-bg)'
                    : 'var(--wa-divider)',
            },
        }}
    >
        {icon}
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500, lineHeight: 1 }}>
            {label}
        </Typography>
        {(count ?? 0) > 0 && (
            <Typography
                sx={{
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    lineHeight: 1,
                    px: 0.5,
                    borderRadius: '999px',
                    color: 'inherit',
                }}
            >
                {count}
            </Typography>
        )}
    </Box>
);

// Compact Archived + Channels entries shown as a single row of badges in the
// chat-list header (replaces the two stacked list rows). Each toggles its
// view; the active one is highlighted, click again returns to the inbox.
const ChatsSideBarFolders = React.memo(() => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const archivedCount = useAppSelector(selectTotalArchivedChatsCount);
    const channelsCount = useAppSelector(selectChannelChatsCount);
    const isArchivedView = useAppSelector(selectIsArchivedView);
    const isChannelsView = useAppSelector(selectIsChannelsView);
    // Read-receipt opt-out — debug-only surface (localStorage['bt-debug']).
    const sendSeenEnabled = useSendSeenEnabled();
    const showSendSeenToggle = isSendSeenToggleVisible();

    return (
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <FolderPill
                label={t('teamInbox.archived')}
                count={archivedCount}
                active={isArchivedView}
                icon={<ArchiveIcon sx={{ fontSize: '1rem' }} />}
                onClick={() => dispatch(TeamInboxActions.setIsArchivedView(!isArchivedView))}
            />
            {channelsCount > 0 && (
                <FolderPill
                    label={t('teamInbox.channels', 'Channels')}
                    count={channelsCount}
                    active={isChannelsView}
                    icon={<CampaignIcon sx={{ fontSize: '1rem' }} />}
                    onClick={() => dispatch(TeamInboxActions.setIsChannelsView(!isChannelsView))}
                />
            )}
            {showSendSeenToggle && (
                <FolderPill
                    label={t('teamInbox.sendReadReceipts', 'Read receipts')}
                    active={sendSeenEnabled}
                    icon={sendSeenEnabled
                        ? <DoneAllIcon sx={{ fontSize: '1rem' }} />
                        : <RemoveDoneIcon sx={{ fontSize: '1rem' }} />}
                    title={sendSeenEnabled
                        ? t('teamInbox.sendReadReceiptsOnHint', 'Opening a chat marks it read on WhatsApp. Click to stop sending read receipts.')
                        : t('teamInbox.sendReadReceiptsOffHint', 'Opening a chat does NOT mark it read. Click to send read receipts again.')}
                    onClick={() => setSendSeenEnabled(!sendSeenEnabled)}
                />
            )}
        </Stack>
    );
});

export default ChatsSideBarFolders;
